import React from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, footer, mobile menu). Each domain gets
// its own finished logo image. They are white logos on transparent
// backgrounds, so `invertOnLight` flips them to black on light surfaces and
// keeps them white on dark surfaces.
const LOGO_BY_REGION: Record<RegionCode, string> = {
  '253': 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
  '206': 'https://cdn.magicpatterns.com/uploads/c3mbuRDas3xEUXsT3VXyzE/206code.png',
  '425': 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
};
/** Resolve the active domain's logo image URL. */
export function getLogoSrc(region?: RegionCode): string {
  return LOGO_BY_REGION[region ?? getRegion()];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * White logo → black on light backgrounds, white on dark backgrounds.
   * Set false where the logo already sits on a dark surface (e.g. footer).
   */
  invertOnLight?: boolean;
  /**
   * Retained for backwards-compatibility with existing call sites. The logo is
   * now a single static brand mark, so these no longer affect rendering.
   */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  /**
   * When true, a colored silhouette of the logo crossfades in over the base
   * mark. Used by the navbar to tint the logo forest-green once it collapses
   * on scroll.
   */
  tinted?: boolean;
  /** Color the tint overlay fills the logo with. Defaults to forest green. */
  tintColor?: string;
}
/**
 * 206Gravel brand mark — a single finished logo image rendered everywhere.
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  tinted = false,
  tintColor = '#1E7A3C'
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const invertClass = invertOnLight ? 'dark:brightness-100 brightness-0' : '';
  const src = getLogoSrc();
  return <span className={`relative inline-block ${className}`} data-id="element-1162" data-mp="1rz5dv3v4v4zb">
      <img src={src} alt="206Gravel" className={`${imgClassName} ${invertClass} block pointer-events-none transition-opacity duration-1000`} style={{
      opacity: tinted ? 0 : 1
    }} data-id="element-1163" data-mp="1wxgkl5q4szdb" />
      {/* Forest-green silhouette that fades in over the base mark. The logo
                              image is used as a CSS mask so the green fills its exact shape. */}
      <span aria-hidden="true" className={`${imgClassName} absolute inset-0 block pointer-events-none transition-opacity duration-1000`} style={{
      opacity: tinted ? 1 : 0,
      backgroundColor: tintColor,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskPosition: 'left center',
      maskPosition: 'left center'
    }} data-id="element-1164" data-mp="7k3dx9ujqz91" />
    </span>;
}